import React, { useEffect, useState } from "react";
import { Card, Col, Row, Spinner, Button } from "reactstrap";
import { CustomInput } from "../../Common/Components/Input/CustomInput";
import { CustomSubmitButton } from "../../Common/Components/Input/CustomSubmitButton";
import useApiCallService from "../../Common/Helpers/ApiCall";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import { ADMIN_PATHS } from "../../config";
import { useNavigate } from "react-router-dom";
import { CustomButton } from "../../Common/Components/Button/CustomButton";
import { useTranslation } from "react-i18next";
import { CustomReload } from "../../AppSystem/Helpers/CustomReload";
import { CustomModal } from "../../Common/Components/Modal/CustomModal";
import AdminRequestAccountingCreate from "../Models/Request/AdminRequestAccountingCreate";
import AccountingModel from "../Models/Response/AccountingModel";
import { useHeader } from "../../Auth/Components/Nav/HeaderContext";
import { CheckMobile } from "../../AppSystem/Helpers/CheckMobile";

export function AdminAccountingCreate() {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();
    const navigate = useNavigate();
    const isMobile = CheckMobile(998);

    const [accounting, setAccounting] = useState(new AdminRequestAccountingCreate());
    const [responseAccounting, setResponseAccounting] = useState(new AccountingModel());
    const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();

    useEffect(() => {
        setPageTitle(t('accounting.create'));
        setPageTitleKey('accounting.create');
        setOnBack(() => { navigate(ADMIN_PATHS.ADMIN_ACCOUNTING_LIST); });
        return () => { setPageTitle(''); setPageTitleKey(''); setOnBack(null); };
    }, [t]);

    const [displaySpinner, setDisplaySpinner] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // validation state
    const [fieldErrors, setFieldErrors] = useState({});
    const [touched, setTouched] = useState({});

    // MODALS
    const [displaySendModal, setDisplaySendModal] = useState(false);

    // GET ID IF EXISTS FOR EDIT
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id') ?? "";

    // yup schema from model
    const schema = AdminRequestAccountingCreate.getAttributes(null, true);

    // Documentation options
    const documentationOptions = AdminRequestAccountingCreate.getDocumentationOptions();

    // handle per-field blur validation
    const handleBlur = async (name) => {
        setTouched(prev => ({ ...prev, [name]: true }));
        try {
            await schema.validateAt(name, accounting);
            // valid -> clear field error
            setFieldErrors(prev => {
                const copy = { ...prev };
                delete copy[name];
                return copy;
            });
        } catch (err) {
            setFieldErrors(prev => ({ ...prev, [name]: err.message }));
        }
    };

    const valueInput = document.getElementById('value');
    if (valueInput) {
        valueInput.addEventListener('keypress', function (e) {
            const key = e.key;

            if (!/^[0-9]$/.test(key)) {
                e.preventDefault();
            return;
            }

            const start = this.selectionStart ?? 0;
            const end = this.selectionEnd ?? 0;
            const newLength = this.value.length - (end - start) + 1;
            if (newLength > 4) {
                e.preventDefault();
            }
        });
    }

    const handleChangeAccounting = (e, name) => {
        let value;

        if (e && e.target) {
            if (e.target.type === "checkbox") value = e.target.checked;
            else value = e.target.value;
        } else if (e && e.value !== undefined) {
            // support for select-like custom inputs
            value = e.value;
        } else {
            value = e ?? "";
        }

        // update model
        setAccounting((prevFormData) => ({
            ...prevFormData,
            [name]: value
        }));

        // clear error for this field as user types
        setFieldErrors(prev => {
            const copy = { ...prev };
            delete copy[name];
            return copy;
        });
    };

    // Handle checkbox changes for required documentation
    const handleDocumentationChange = (docValue, isChecked) => {
        setAccounting((prev) => {
            const currentDocs = prev.requiredDocumentation || [];
            let newDocs;

            if (isChecked) {
                // Add if not already present
                if (!currentDocs.includes(docValue)) {
                    newDocs = [...currentDocs, docValue];
                } else {
                    newDocs = currentDocs;
                }
            } else {
                // Remove
                newDocs = currentDocs.filter(doc => doc !== docValue);
            }

            return {
                ...prev,
                requiredDocumentation: newDocs
            };
        });
    };

    const handleValidation = async () => {
        setDisplaySpinner(true);

        try {
            await schema.validate(accounting, { abortEarly: false });

            let url = `/accounting/create?name=${encodeURIComponent(accounting.name ?? "")}&value=${encodeURIComponent(accounting.value ?? "")}&message=${encodeURIComponent(accounting.message ?? "")}`;
            
            const reqDocs = accounting.requiredDocumentation || [];
            reqDocs.forEach((doc, index) => {
                url += `&requiredDocumentation[${index}]=${encodeURIComponent(doc)}`;
            });

            const apiCallOptions = {
                method: "post",
                url: url,
            };

            try {
                await ApiCall(apiCallOptions, setResponseAccounting, setErrorMessage);
            } catch (error) {
                setDisplaySpinner(false);
                console.error(error);
                FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
            }
        } catch (err) {
            // Yup validation failed -> collect field errors
            const newErrors = {};
            if (err && err.inner && err.inner.length > 0) {
                err.inner.forEach(e => {
                    if (e.path) newErrors[e.path] = e.message;
                });
            } else if (err && err.path) {
                newErrors[err.path] = err.message;
            }

            setFieldErrors(newErrors);
            const newTouched = { ...touched };
            Object.keys(newErrors).forEach(k => { newTouched[k] = true; });
            setTouched(newTouched);

            setDisplaySpinner(false);
        }
    };

    useEffect(() => {
        if (errorMessage !== '') {
            if (errorMessage.includes('validationMessage.accountExists') || errorMessage.includes('validationMessage.nameExists')) {
                FlashMessage(t(errorMessage), 'flash-messageError');
            } else {
                FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
            }
            setDisplaySpinner(false);
            setErrorMessage('');
            setTimeout(() => { CustomReload(); }, 4800);
        }
    }, [errorMessage, t]);

    // REDIRECT TO LIST PAGE
    useEffect(() => {
        if (responseAccounting && responseAccounting.id !== "") {
            // Verificar si es un mensaje de error disfrazado de éxito
            if (typeof responseAccounting === 'string' && responseAccounting.includes('validation.duplicate')) {
                
                const errorMsg = responseAccounting;

                if (errorMsg.includes('validation.duplicateValue')) {
                    FlashMessage(t('validationMessage.accountExists'), 'flash-messageError');
                    setFieldErrors(prev => ({ ...prev, value: 'validationMessage.accountExists' }));
                    setTouched(prev => ({ ...prev, value: true }));
                } else if (errorMsg.includes('validation.duplicateName')) {
                    FlashMessage(t('validationMessage.nameExists'), 'flash-messageError');
                    setFieldErrors(prev => ({ ...prev, name: 'validationMessage.nameExists' }));
                    setTouched(prev => ({ ...prev, name: true }));
                } else if (errorMsg.includes('validation.duplicateEntry')) {
                    FlashMessage(t('validationMessage.duplicateEntry'), 'flash-messageError');
                    setFieldErrors(prev => ({ 
                        ...prev, 
                        name: 'validationMessage.duplicateEntry',
                        value: 'validationMessage.duplicateEntry'
                    }));
                    setTouched(prev => ({ ...prev, name: true, value: true }));
                } else {
                    FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
                }
                
                setDisplaySpinner(false);
                // Resetear responseAccounting para permitir reintentos
                setResponseAccounting(new AccountingModel());
                
            } else {
                // Es un éxito real (id es un ObjectId válido de MongoDB)
                FlashMessage(t('validationMessage.accountingSuccessfully'), 'flash-messageSuccess');
                setTimeout(() => {
                    navigate(`${ADMIN_PATHS.ADMIN_ACCOUNTING_LIST}`);
                }, 2000);
            }
        }
    }, [responseAccounting, navigate, t]);

    // GET ACCOUNTING IF EXISTS (EDIT)
    useEffect(() => {
        if (id !== "") {
            const handleApiResponse = (apiResponse) => {
                setAccounting(new AdminRequestAccountingCreate(apiResponse));
            };
            const fetchData = async () => {
                try {
                    const apiCallOptions = {
                        method: "get",
                        url: `/accounting?id=${id}`
                    };
                    await ApiCall(apiCallOptions, handleApiResponse);
                } catch (error) {
                    console.log('error', error);
                }
            };
            fetchData();
        }
    }, [id, ApiCall]);

    const modalSendContent = (
        <div id="sendMessageDiv">
            {t('accounting.accountingModalSend')}
        </div>
    );

    const modalSendFooter = (
        <>
            <Button className="box-green border-0" id="sendButton" onClick={async () => {
                setDisplaySendModal(false);
                await handleValidation();
            }}>
                {t('button.create')}
            </Button>
            <Button className="box-red border-0" onClick={() => {
                setDisplaySendModal(false);
            }}>
                {t('button.cancel')}
            </Button>
        </>
    );

    const renderFieldErrorAbove = (fieldName) => {
        const err = fieldErrors[fieldName];
        if (!err || !touched[fieldName]) return null;
        const translated = typeof err === 'string' ? t(err) : err;
        return (
            <div className={`error tooltip-custom-normal position-absolute ${err ? 'opacity-1' : 'opacity-0'}`}
                style={{color: '#f56e6e'}}>
                {translated}
            </div>
        );
    };

    return (
        <>
            <CustomModal
                isOpen={displaySendModal}
                toggle={() => setDisplaySendModal(!displaySendModal)}
                body={modalSendContent}
                footer={modalSendFooter}
            />
            {!isMobile && (
                <Col>
                    <CustomButton
                        text={t('button.back')}
                        icon={'bx bx-chevron-left'}
                        buttonSize={'small'}
                        customClasses={'mb-3 ms-3'}
                        onClick={() => {
                            navigate(ADMIN_PATHS.ADMIN_ACCOUNTING_LIST);
                        }}
                    />
                </Col>
            )}
            <Row className="pb-4 px-0 mx-0">
                <Col lg={6} className="px-0 mx-0 mb-3 pe-lg-2">
                    <Card className="card-backend p-4">
                        <Row>
                            <Col xs='auto'>
                                <h2 className='rokkitt-font'>{t('accounting.create')}</h2>
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={12}>
                                <div className={'mb-3 position-relative'} style={{ paddingTop: '10px' }}>
                                    {renderFieldErrorAbove('value')}
                                    <CustomInput
                                        model={AdminRequestAccountingCreate}
                                        attribute='value'
                                        data={accounting.value}
                                        customChange={(e) => handleChangeAccounting(e, 'value')}
                                        inputProps={{ id: 'value', onBlur: () => handleBlur('value') }}
                                    />
                                </div>
                            </Col>

                            <Col lg={12}>
                                <div className={'mb-3 position-relative'} style={{ paddingTop: '10px' }}>
                                    {renderFieldErrorAbove('name')}
                                    <CustomInput
                                        model={AdminRequestAccountingCreate}
                                        attribute='name'
                                        data={accounting.name}
                                        customChange={(e) => handleChangeAccounting(e, 'name')}
                                        inputProps={{ id: 'name', onBlur: () => handleBlur('name') }}
                                    />
                                </div>
                            </Col>

                            <Col lg={12}>
                                <div className="mb-3">
                                    <label>{t('accounting.message')}</label>
                                    <textarea
                                        className="form-control"
                                        value={accounting.message || ''}
                                        onChange={(e) => handleChangeAccounting(e, "message")}
                                        style={{ resize: "vertical", minHeight: "60px", width: "100%", overflow: "auto" }}
                                        rows={2}
                                    />
                                </div>
                            </Col>

                            <Col lg={12} className="text-end pt-5">
                                {displaySpinner === true ? (
                                    <>
                                        {t('validationMessage.creatingAccounting')} <Spinner color='success' className="me-3" />
                                    </>
                                ) : ''}
                                <CustomSubmitButton
                                    text={t('button.create')}
                                    id='submitAccounting'
                                    icon='bx bx-save pe-2'
                                    customClick={async () => {
                                        if (displaySpinner) return;

                                        try {
                                            await schema.validate(accounting, { abortEarly: false });
                                            setDisplaySendModal(true);
                                        } catch (err) {
                                            const newErrors = {};
                                            if (err && err.inner && err.inner.length > 0) {
                                                err.inner.forEach(e => {
                                                    if (e.path) newErrors[e.path] = e.message;
                                                });
                                            } else if (err && err.path) {
                                                newErrors[err.path] = err.message;
                                            }

                                            setFieldErrors(newErrors);

                                            const newTouched = { ...touched };
                                            Object.keys(newErrors).forEach(k => { newTouched[k] = true; });
                                            setTouched(newTouched);
                                        }
                                    }}
                                />
                            </Col>
                        </Row>
                    </Card>
                </Col>

                {/* RIGHT SIDE: Required Documentation */}
                <Col lg={6} className="px-0 mx-0 ps-lg-2 mb-4">
                    <Card className="card-backend p-4">
                        <Row>
                            <Col xs='auto'>
                                <h3 className='rokkitt-font'>{t('accounting.requiredDocumentation')}</h3>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col lg={12}>
                                <p className="text-muted mb-3">{t('accounting.selectRequiredDocs')}</p>
                                {documentationOptions.map((option) => (
                                    <div key={option.value} className="form-check mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`doc-${option.value}`}
                                            checked={(accounting.requiredDocumentation || []).includes(option.value)}
                                            onChange={(e) => handleDocumentationChange(option.value, e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor={`doc-${option.value}`}>
                                            {t(option.label)}
                                        </label>
                                    </div>
                                ))}
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </>
    );
}

export default AdminAccountingCreate;